import { useMutation } from '@apollo/client'
import { datadogLogs } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum'
import { useCallback, useEffect, useState } from 'react'

import { RETRIEVE_CHAT_USER_REFRESH_TOKEN } from './mutations'

/**
 * Manages the Sendbird session token needed to authenticate a user with the Sendbird SDK.
 *
 * Sendbird requires a short-lived session token (distinct from the app's auth token) to
 * establish a connection. This hook fetches that token via the `retrieveChatUserToken`
 * GraphQL mutation on mount (if no token is cached) and exposes it alongside helpers for
 * manually refreshing or overriding it — useful for reconnect flows or token expiry handling.
 *
 * Returns:
 *  - `token`         — the current Sendbird session token, or null if not yet fetched
 *  - `setToken`      — override the token directly (e.g. to clear it on disconnect)
 *  - `retrieveToken` — imperatively re-fetch the token; returns the new token or null on failure
 */
export const useSendbirdToken = () => {
  const [token, setToken] = useState<string | null>(null)
  const [retrieveTokenMutation] = useMutation(RETRIEVE_CHAT_USER_REFRESH_TOKEN)

  const retrieveToken = useCallback(async (): Promise<string | null> => {
    try {
      const response = await retrieveTokenMutation()
      const newToken = response.data?.retrieveChatUserToken?.token ?? null
      if (newToken) {
        setToken(newToken)
      } else {
        datadogLogs.logger.warn(
          '[useSendbirdToken] Token retrieval returned null — mutation succeeded but no token in response'
        )
      }
      return newToken
    } catch (error) {
      datadogRum.addError(error as Error, {
        source: 'sendbird-token',
        action: 'retrieve_token',
      })
      return null
    }
  }, [retrieveTokenMutation])

  useEffect(() => {
    if (!token) {
      retrieveToken()
    }
  }, [token, retrieveToken])

  return { token, setToken, retrieveToken }
}
